/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Mic, MicOff, Sparkles, RefreshCw, Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useWebSpeech from '@/lib/useWebSpeech';
import { SPEECH_LANG_TAGS } from '@/lib/dictationParser';
import { feedSoapChunk, emptySoapState, formatSoapAsText } from '@/lib/soapParser';
import { LANG_OPTIONS, STORAGE_KEY, SECTION_LABELS, readSavedLang } from './soap/soapConfig';
import { SoapPreviewGrid, SoapHistory } from './soap/SoapPreview';

/**
 * VoiceToSoapButton — hands-free "Detta nota SOAP" widget for the clinical
 * notes panel. Continuously listens to the doctor and routes each spoken
 * fragment into the correct SOAP section using keyword triggers (IT/EN/ES).
 *
 * Props:
 *   - onSoapUpdate({ soap, formattedNotes, changedSections })
 *       Fired each time a chunk is committed. The parent decides how to
 *       merge the SOAP block into its Notes / Diagnosis / Rx text fields.
 *   - initialLang? — optional default language ('it' | 'en' | 'es' | 'auto')
 */

export const VoiceToSoapButton = ({
  onSoapUpdate,
  testId = 'soap-dictate-btn',
}) => {
  const [lang, setLang] = useState(readSavedLang);
  const [soap, setSoap] = useState(emptySoapState);
  const [history, setHistory] = useState([]); // [{id, at, section, text, lang}]
  const [historyOpen, setHistoryOpen] = useState(false);
  const soapRef = useRef(soap);
  useEffect(() => { soapRef.current = soap; }, [soap]);

  // Effective recognition language: use IT-IT when user picked 'auto' so the
  // recognizer still gets an accented model; the SOAP parser then falls back
  // to auto-detect on the text.
  const speechLang = SPEECH_LANG_TAGS[lang] || SPEECH_LANG_TAGS.it;

  const {
    supported, listening, interim, start, stop, reset,
  } = useWebSpeech({
    lang: speechLang,
    continuous: true,
    interim: true,
    onFinal: (chunk) => {
      const { state: next, changedSections, lang: detectedLang } = feedSoapChunk(
        soapRef.current,
        chunk,
        { lang },
      );
      setSoap(next);
      soapRef.current = next;
      const formatted = formatSoapAsText(next);
      // Push each spoken chunk into the timeline. If the chunk touched more
      // than one section (rare) we still record it as a single entry with
      // the primary target so the doctor can replay who-said-what.
      const primarySection = (changedSections && changedSections[0]) || next.lastSection || 'subjective';
      const entry = {
        id: `soap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        at: new Date().toISOString(),
        section: primarySection,
        sections: changedSections,
        text: chunk,
        lang: detectedLang || lang,
      };
      setHistory((prev) => [...prev, entry]);
      if (typeof onSoapUpdate === 'function') {
        onSoapUpdate({
          soap: next,
          formattedNotes: formatted,
          changedSections,
          historyEntry: entry,
        });
      }
      if (changedSections.length > 0) {
        const labels = changedSections.map((s) => SECTION_LABELS[s]?.label || s).join(' + ');
        toast.success(`SOAP aggiornato · ${labels}`);
      }
    },
  });

  useEffect(() => {
    try { window.localStorage?.setItem(STORAGE_KEY, lang); } catch (e) { /* ignore */ }
  }, [lang]);

  const changeLang = useCallback((next) => {
    if (listening) {
      try { stop(); } catch (e) { /* ignore */ }
    }
    setLang(next);
  }, [listening, stop]);

  const toggleListening = () => {
    if (listening) {
      stop();
      toast.info('Dettatura SOAP interrotta');
    } else {
      reset();
      start();
      toast.info('Detta pure — dì "Sintomi", "Obiettivo", "Diagnosi" o "Piano" per cambiare sezione');
    }
  };

  const resetSoap = () => {
    setSoap(emptySoapState());
    soapRef.current = emptySoapState();
    setHistory([]);
    reset();
    if (typeof onSoapUpdate === 'function') {
      onSoapUpdate({ soap: emptySoapState(), formattedNotes: '', changedSections: [], historyEntry: null });
    }
    toast.info('Blocco SOAP cancellato');
  };

  const current = LANG_OPTIONS.find((o) => o.code === lang) || LANG_OPTIONS[0];
  const hasContent = !!(soap.subjective || soap.objective || soap.assessment || soap.plan);

  if (!supported) {
    return (
      <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground flex items-center gap-2" data-testid={`${testId}-unsupported`}>
        <MicOff className="h-4 w-4" />
        Dettatura SOAP non disponibile. Usa Chrome, Edge o Safari.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="voice-to-soap">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm">Dettatura SOAP</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            onClick={toggleListening}
            size="sm"
            variant={listening ? 'default' : 'outline'}
            className={listening ? 'animate-pulse' : ''}
            data-testid={testId}
          >
            {listening ? <Mic className="h-3.5 w-3.5 mr-1 text-red-500" /> : <Mic className="h-3.5 w-3.5 mr-1" />}
            {listening ? 'In ascolto…' : 'Detta SOAP'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid={`${testId}-lang`} title={`Lingua: ${current.label}`}>
                <Languages className="h-3.5 w-3.5 mr-1" />
                <span className="font-mono text-[10px]">{current.shortLabel}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Lingua dettatura
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LANG_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.code}
                  onSelect={() => changeLang(opt.code)}
                  data-testid={`${testId}-lang-${opt.code}`}
                  className={opt.code === lang ? 'bg-primary/10 font-semibold' : ''}
                >
                  <span className="mr-2 text-base leading-none">{opt.flag}</span>
                  <span className="flex-1">{opt.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {hasContent && (
            <Button
              variant="ghost"
              size="icon"
              onClick={resetSoap}
              title="Ricomincia il blocco SOAP"
              data-testid={`${testId}-reset`}
              aria-label="Ricomincia SOAP"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {listening && interim && (
        <Badge variant="outline" className="text-[11px] font-mono max-w-full truncate" data-testid={`${testId}-interim`}>
          “{interim}”
        </Badge>
      )}

      {hasContent && <SoapPreviewGrid soap={soap} />}

      {!hasContent && !listening && (
        <p className="text-[11px] text-muted-foreground leading-snug">
          Premi <b>Detta SOAP</b> e parla naturalmente. Le parole
          &quot;<span className="font-mono">Sintomi</span>&quot;,
          &quot;<span className="font-mono">Obiettivo</span>&quot;,
          &quot;<span className="font-mono">Diagnosi</span>&quot;,
          &quot;<span className="font-mono">Piano</span>&quot; (o l'equivalente EN/ES)
          fanno passare alla sezione successiva.
        </p>
      )}

      <SoapHistory history={history} open={historyOpen} onOpenChange={setHistoryOpen} />
    </div>
  );
};

export default VoiceToSoapButton;
